import 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {TSummaryPage} from '@/pages/components/audit-program/t-section/constants';
import {TS_PAGE} from '@/pages/components/audit-program/t-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface TsSummaryViewProps {
    header: AuditProgramSectionVO;
    page: TSummaryPage;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
const bodySx = {...cellBorder, py: 0.5, px: 0.5, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** Ts — Summary sheet for subsequent events objectives. */
export default function TsSummaryView({
    header,
    page,
    refCode = 'Ts',
    values,
    onChange,
}: TsSummaryViewProps) {
    const meta = {
        title: page.title || TS_PAGE.title,
        objectivesTitle: page.objectivesTitle || TS_PAGE.objectivesTitle,
        objectives: page.objectives?.length ? page.objectives : TS_PAGE.objectives,
        planningTitle: page.planningTitle || TS_PAGE.planningTitle,
        finalTitle: page.finalTitle || TS_PAGE.finalTitle,
        materialityLabel: page.materialityLabel || TS_PAGE.materialityLabel,
        planningNote: page.planningNote || TS_PAGE.planningNote,
        finalBullets: page.finalBullets?.length ? page.finalBullets : TS_PAGE.finalBullets,
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 1}}>
                {meta.objectivesTitle}
            </Typography>
            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 720, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 36}}/>
                        <col/>
                        <col style={{width: 120}}/>
                        <col style={{width: 100}}/>
                        <col style={{width: 160}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx}>Objective</TableCell>
                            <TableCell sx={headSx}>Key tests</TableCell>
                            <TableCell sx={headSx}>Objectives met?</TableCell>
                            <TableCell sx={headSx}>Comments</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {meta.objectives.map((obj) => (
                            <TableRow key={obj.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2">{obj.num}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2">{obj.text}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        sx={fieldSx}
                                        value={values[`${obj.id}.tests`] ?? ''}
                                        onChange={(e) => onChange(`${obj.id}.tests`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        align="center"
                                        sx={fieldSx}
                                        placeholder="Yes/No"
                                        value={values[`${obj.id}.met`] ?? ''}
                                        onChange={(e) => onChange(`${obj.id}.met`, e.target.value)}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        sx={fieldSx}
                                        value={values[`${obj.id}.comments`] ?? ''}
                                        onChange={(e) => onChange(`${obj.id}.comments`, e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {meta.planningTitle}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1, maxWidth: 420}}>
                <Typography variant="body2" sx={{minWidth: 140}}>{meta.materialityLabel}</Typography>
                <InputField
                    type="text"
                    fullWidth
                    compact
                    sx={fieldSx}
                    value={values.materiality ?? ''}
                    onChange={(e) => onChange('materiality', e.target.value)}
                />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {meta.planningNote}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {meta.finalTitle}
            </Typography>
            <Typography variant="body2" sx={{mb: 0.5}}>
                From the audit work carried out I confirm that:
            </Typography>
            <Stack spacing={0.25} sx={{mb: 1.5, pl: 1}}>
                {meta.finalBullets.map((b, idx) => (
                    <Typography key={idx} variant="body2">- {b}</Typography>
                ))}
            </Stack>
            <InputField
                type="text"
                fullWidth
                compact
                multiline
                minRows={2}
                placeholder="Additional comments"
                value={values.finalComments ?? ''}
                onChange={(e) => onChange('finalComments', e.target.value)}
                sx={{maxWidth: 720}}
            />
        </Box>
    );
}
